/**
 * AsyncArea — one area of a page that loads data, and the only place that
 * decides what that area shows while it does.
 *
 *   const usersQuery = useQuery(usersQueryOptions());
 *   <AsyncArea
 *     query={usersQuery}
 *     label="users"
 *     isEmpty={(users) => users.length === 0}
 *     empty={<p>No users yet</p>}
 *   >
 *     {(users) => <UserList users={users} />}
 *   </AsyncArea>
 *
 *   // A fetch that is not a TanStack query:
 *   <AsyncArea status={status} error={error} onRetry={load} label="prices">
 *     <PriceTable prices={prices} />
 *   </AsyncArea>
 *
 * States, in the same footprint:
 *  - pending (or retrying after a failure with nothing on screen): the
 *    Spinner with "Loading <label>…", straight away — no delay;
 *  - error with no data: an Alert reading "Couldn't load <label>", the
 *    reason from getErrorMessage, and a Retry button;
 *  - error with data still on screen (a failed refetch, e.g. polling): the
 *    content stays and a slim "Couldn't refresh · Retry" line sits above it;
 *  - success: `empty` when `isEmpty` says so, otherwise the children.
 *
 * The empty state only ever follows a successful load, so "there is no
 * data" can never stand in for "couldn't load".
 */

import type { ReactNode } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { cn } from '@/lib/utils';
import { getErrorMessage } from '@/utils/errorUtils';
import { Button } from '../Button/Button';
import { Spinner } from '../Spinner/Spinner';
import { loadFailedTitle, loadingMessage } from './copy';

export type AsyncStatus = 'pending' | 'error' | 'success';

/** The parts of a TanStack query result AsyncArea reads. Any `useQuery`
 * result satisfies it. */
export type AsyncAreaQuery<TData> = Pick<
  UseQueryResult<TData, unknown>,
  'status' | 'data' | 'error' | 'isFetching' | 'refetch'
>;

interface AsyncAreaCommonProps<TData> {
  /** What the area holds, e.g. 'users': "Loading users…" / "Couldn't load users". */
  label?: string;
  /** True (or a predicate on the loaded data) when the load succeeded with nothing to show. */
  isEmpty?: boolean | ((data: TData) => boolean);
  /** Shown instead of the children when `isEmpty` holds after a successful load. */
  empty?: ReactNode;
  /** The content. A function receives the loaded data. */
  children: ReactNode | ((data: TData) => ReactNode);
  /** Spinner size. Defaults to 'md' in block layout, 'sm' inline. `sm` also
   * switches the error to its compact one-line form, for stat tiles and
   * other small slots. */
  size?: 'sm' | 'md' | 'lg';
  /** `block` (default) centres the spinner/error in a padded area; `inline`
   * renders them without padding, for card headers and small slots. */
  layout?: 'block' | 'inline';
  className?: string;
}

export interface AsyncAreaQueryProps<TData> extends AsyncAreaCommonProps<TData> {
  query: AsyncAreaQuery<TData>;
  status?: never;
  error?: never;
  onRetry?: never;
  data?: never;
}

export interface AsyncAreaStatusProps<TData> extends AsyncAreaCommonProps<TData> {
  query?: never;
  status: AsyncStatus;
  error?: unknown;
  /** Called by the Retry button. Without it, no Retry is offered. */
  onRetry?: () => void;
  /** The loaded data, passed to function children. Data present alongside
   * an error means a failed refresh: the content stays on screen. */
  data?: TData;
}

export type AsyncAreaProps<TData> = AsyncAreaQueryProps<TData> | AsyncAreaStatusProps<TData>;

export interface AsyncAreaErrorProps {
  /** The failure; its sentence comes from getErrorMessage. */
  error: unknown;
  /** Heading. Defaults to "Couldn't load <label>". Actions pass their own,
   * e.g. "Couldn't save template". */
  title?: string;
  label?: string;
  /** Called by the Retry button. Without it, no Retry is offered. */
  onRetry?: () => void;
  /** Shows the Retry button's spinner while a retry is in flight. */
  retrying?: boolean;
  /** `sm` is the compact one-line form for small slots (stat tiles). */
  size?: 'sm' | 'md';
  className?: string;
}

/**
 * The error body every async surface shares: AsyncArea, the DataTable error
 * row, and form/modal action errors (without Retry).
 *
 * Only the icon and title are red; the reason line is muted, so a page with
 * several failed areas does not turn into a wall of red.
 *
 * `size="sm"` is the compact form for small slots such as stat tiles: one
 * line — icon, "Couldn't load", an icon-only Retry — with no bordered box.
 * The full title and reason sit in the tooltip and are read to screen readers.
 */
export function AsyncAreaError({
  error,
  title,
  label,
  onRetry,
  retrying = false,
  size = 'md',
  className,
}: AsyncAreaErrorProps) {
  const heading = title ?? loadFailedTitle(label);
  const reason = getErrorMessage(error);

  if (size === 'sm') {
    return (
      <div
        role="alert"
        data-slot="async-area-error"
        title={`${heading}: ${reason}`}
        className={cn('flex items-center gap-1.5 text-sm', className)}
      >
        <AlertCircle aria-hidden className="size-4 shrink-0 text-destructive" />
        <span className="font-medium text-destructive">{title ?? <>Couldn&apos;t load</>}</span>
        <span className="sr-only">
          {!title && label ? ` ${label}` : ''}: {reason}
        </span>
        {onRetry && (
          <Button
            icon={RefreshCw}
            aria-label="Retry"
            variant="ghost"
            size="xs"
            loading={retrying}
            onClick={onRetry}
          />
        )}
      </div>
    );
  }

  return (
    <Alert
      variant="destructive"
      className={cn('items-start *:data-[slot=alert-description]:text-muted-foreground', className)}
    >
      <AlertCircle />
      <AlertTitle>{heading}</AlertTitle>
      <AlertDescription>
        <p>{reason}</p>
        {onRetry && (
          <Button
            label="Retry"
            preIcon={RefreshCw}
            variant="outline"
            size="sm"
            loading={retrying}
            onClick={onRetry}
            className="mt-2"
          />
        )}
      </AlertDescription>
    </Alert>
  );
}

export interface AsyncAreaRefreshErrorProps {
  error: unknown;
  onRetry?: () => void;
  retrying?: boolean;
  className?: string;
}

/**
 * The slim line for a failed refresh while the last good data stays on
 * screen. The reason is in the tooltip and for screen readers.
 */
export function AsyncAreaRefreshError({ error, onRetry, retrying = false, className }: AsyncAreaRefreshErrorProps) {
  const reason = getErrorMessage(error);
  return (
    <div
      role="status"
      title={reason}
      className={cn('flex items-center gap-1.5 text-sm text-destructive', className)}
    >
      <AlertCircle aria-hidden className="size-4 shrink-0" />
      <span>Couldn&apos;t refresh</span>
      <span className="sr-only">: {reason}</span>
      {onRetry && (
        <>
          <span aria-hidden>·</span>
          <Button label="Retry" variant="link" size="xs" loading={retrying} onClick={onRetry} className="h-auto p-0" />
        </>
      )}
    </div>
  );
}

export function AsyncArea<TData>(props: AsyncAreaProps<TData>) {
  const { label, isEmpty, empty, children, size, layout = 'block', className } = props;

  let status: AsyncStatus;
  let data: TData | undefined;
  let error: unknown;
  let fetching: boolean;
  let retry: (() => void) | undefined;
  if (props.query) {
    const { query } = props;
    status = query.status;
    data = query.data;
    error = query.error;
    fetching = query.isFetching;
    retry = () => {
      void query.refetch();
    };
  } else {
    status = props.status;
    data = props.data;
    error = props.error;
    fetching = false;
    retry = props.onRetry;
  }

  const hasData = data !== undefined;
  const block = layout === 'block';

  // A retry after a failure with nothing on screen reads as loading again.
  if (status === 'pending' || (status === 'error' && !hasData && fetching)) {
    return (
      <div data-slot="async-area" data-state="pending" aria-busy="true" className={className}>
        <Spinner layout={layout} size={size} message={loadingMessage(label)} />
      </div>
    );
  }

  if (status === 'error' && !hasData) {
    if (size === 'sm') {
      return (
        <div data-slot="async-area" data-state="error" className={className}>
          <AsyncAreaError error={error} label={label} onRetry={retry} size="sm" />
        </div>
      );
    }
    return (
      <div
        data-slot="async-area"
        data-state="error"
        className={cn(block && 'flex justify-center py-8', className)}
      >
        <AsyncAreaError error={error} label={label} onRetry={retry} className={block ? 'max-w-lg' : undefined} />
      </div>
    );
  }

  const showEmpty =
    status === 'success' &&
    empty !== undefined &&
    (typeof isEmpty === 'function' ? isEmpty(data as TData) : isEmpty === true);

  return (
    <div data-slot="async-area" data-state={status} className={className}>
      {status === 'error' && (
        <AsyncAreaRefreshError error={error} onRetry={retry} retrying={fetching} className="mb-2" />
      )}
      {showEmpty ? empty : typeof children === 'function' ? children(data as TData) : children}
    </div>
  );
}
